import { useState, useRef } from 'react'
import { Send, ImagePlus, X } from 'lucide-react'
import { clsx } from 'clsx'
import { CommentItem } from './CommentItem'
import { uploadCommentImage } from '@/services/sharedGroupService'
import type { TaskComment, SharedGroupMember } from '@/lib/sharedTypes'

interface CommentSectionProps {
  groupId: string
  taskId: string
  comments: TaskComment[]
  onAdd: (text: string, mentions?: string[], parentId?: string, image?: { url: string; width: number; height: number }) => Promise<void>
  onDelete: (commentId: string) => void
  members: SharedGroupMember[]
  currentUid?: string
}

function parseMentions(text: string, members: SharedGroupMember[]): string[] {
  const mentionPattern = /@(\S+)/g
  const matched: string[] = []
  let m: RegExpExecArray | null

  while ((m = mentionPattern.exec(text)) !== null) {
    const mentionName = m[1]
    const member = members.find(
      (mb) => mb.displayName === mentionName || mb.displayName.replace(/\s+/g, '') === mentionName
    )
    if (member && !matched.includes(member.uid)) {
      matched.push(member.uid)
    }
  }

  return matched
}

export function CommentSection({ groupId, taskId, comments, onAdd, onDelete, members, currentUid }: CommentSectionProps) {
  const [text, setText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [replyTo, setReplyTo] = useState<{ id: string; userName: string } | null>(null)
  const [pendingImage, setPendingImage] = useState<{ file: File; previewUrl: string } | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSend = async () => {
    const trimmed = text.trim()
    if (!trimmed && !pendingImage) return

    setIsSending(true)
    try {
      const mentions = parseMentions(trimmed, members)
      let image: { url: string; width: number; height: number } | undefined
      if (pendingImage) {
        image = await uploadCommentImage(groupId, taskId, pendingImage.file)
      }
      await onAdd(
        trimmed || ' ',
        mentions.length > 0 ? mentions : undefined,
        replyTo?.id,
        image,
      )
      setText('')
      setReplyTo(null)
      clearPendingImage()
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value)
    const ta = e.target
    ta.style.height = 'auto'
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px'
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    clearPendingImage()
    const previewUrl = URL.createObjectURL(file)
    setPendingImage({ file, previewUrl })
    e.target.value = ''
  }

  const clearPendingImage = () => {
    if (pendingImage) {
      URL.revokeObjectURL(pendingImage.previewUrl)
      setPendingImage(null)
    }
  }

  const sortedComments = [...comments].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  // Group into root + replies
  const rootComments = sortedComments.filter((c) => !c.parentId)
  const repliesByParent = new Map<string, TaskComment[]>()
  for (const c of sortedComments) {
    if (c.parentId) {
      const existing = repliesByParent.get(c.parentId) || []
      existing.push(c)
      repliesByParent.set(c.parentId, existing)
    }
  }

  return (
    <div className="space-y-3">
      {/* Comment list */}
      {rootComments.length > 0 ? (
        <div className="space-y-0.5 max-h-72 overflow-y-auto">
          {rootComments.map((comment) => (
            <div key={comment.id}>
              <CommentItem
                comment={comment}
                isOwn={comment.userId === currentUid}
                onDelete={() => onDelete(comment.id)}
                onReply={() => setReplyTo({ id: comment.id, userName: comment.userName })}
              />
              {/* Replies */}
              {repliesByParent.get(comment.id)?.map((reply) => (
                <div key={reply.id} className="ml-10">
                  <CommentItem
                    comment={reply}
                    isOwn={reply.userId === currentUid}
                    isReply
                    onDelete={() => onDelete(reply.id)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-caption text-center py-4">
          아직 댓글이 없습니다
        </p>
      )}

      {/* Reply-to indicator */}
      {replyTo && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-tertiary text-xs text-caption">
          <span className="flex-1 truncate">
            {replyTo.userName}에게 답글
          </span>
          <button onClick={() => setReplyTo(null)} className="text-disabled hover:text-heading">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Image preview */}
      {pendingImage && (
        <div className="relative inline-block">
          <img
            src={pendingImage.previewUrl}
            alt="preview"
            className="max-w-[160px] max-h-[120px] rounded-lg object-cover border border-base"
          />
          <button
            onClick={clearPendingImage}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Input */}
      <div className="flex items-end gap-2 border border-base rounded-lg bg-surface-primary p-2">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextareaInput}
          onKeyDown={handleKeyDown}
          placeholder={replyTo ? '답글 입력...' : '댓글 입력... (@로 멘션)'}
          rows={1}
          disabled={isSending}
          className={clsx(
            'flex-1 text-sm bg-transparent border-none outline-none resize-none',
            'text-heading placeholder:text-disabled',
            'min-h-[1.5rem] max-h-[7.5rem]'
          )}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageSelect}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isSending}
          className="p-1.5 rounded-lg transition-colors text-caption hover:text-heading flex-shrink-0"
        >
          <ImagePlus className="w-4 h-4" />
        </button>
        <button
          onClick={handleSend}
          disabled={(!text.trim() && !pendingImage) || isSending}
          className={clsx(
            'p-1.5 rounded-lg transition-colors flex-shrink-0',
            (text.trim() || pendingImage)
              ? 'text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20'
              : 'text-disabled'
          )}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
